import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { esGestorComercial } from '../utils/permissions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip as ShadcnTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  ClipboardList, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Users, 
  Briefcase,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronDown,
  Bell,
  Calendar,
  BarChart3,
  PieChart,
  Target,
  Award,
  RefreshCw
} from 'lucide-react';
import api, { settleRequests } from '../utils/api';
import { toast } from 'sonner';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';

const Dashboard = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [metricas, setMetricas] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [metricasVentas, setMetricasVentas] = useState(null);
  
  // Filtros
  const [rangoFecha, setRangoFecha] = useState('este_mes');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [areaSeleccionada, setAreaSeleccionada] = useState('all');
  const [funcionarioSeleccionado, setFuncionarioSeleccionado] = useState('');
  
  // Drill-down
  const [expandedAreas, setExpandedAreas] = useState({});

  // Colores de la paleta Premium Peritum
  const COLORS = {
    azulElectrico: '#3A86FF',
    cyanBrillante: '#00D4FF',
    verdeEsmeralda: '#00E676',
    ambar: '#FFB300',
    rojo: '#FF5252',
    gris: '#64748b'
  };

  const PIE_COLORS = [COLORS.azulElectrico, COLORS.verdeEsmeralda, COLORS.ambar, COLORS.rojo, COLORS.gris];

  // Mapeo de nombres personalizados por email
  const nombresPersonalizados = {
    'jmoraga@peritum.cl': 'Juan Carlos',
    'aconejera@peritum.cl': 'Ana Karina',
    'jcarrasco@peritum.cl': 'Juan Ignacio',
    'gflores@peritum.cl': 'Gustavo Flores',
    'gpoblete@peritum.cl': 'Gemita'
  };

  // Verificar si es socio o gestor comercial para mostrar widget de ventas
  const esSocioVentas = esGestorComercial(currentUser);

  // Obtener nombre para mostrar
  const getNombreMostrar = () => {
    if (!currentUser) return 'Usuario';
    const nombrePersonalizado = nombresPersonalizados[currentUser.email];
    if (nombrePersonalizado) return nombrePersonalizado;
    // Si no tiene nombre personalizado, usar el primer nombre
    return currentUser.name?.split(' ')[0] || 'Usuario';
  };

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, rangoFecha, fechaInicio, fechaFin, areaSeleccionada, funcionarioSeleccionado]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching user');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Calcular fechas según rango seleccionado
      let inicio = '', fin = '';
      const now = new Date();
      
      switch (rangoFecha) {
        case 'este_mes':
          inicio = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
          fin = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
          break;
        case 'mes_anterior':
          inicio = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();
          fin = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();
          break;
        case 'ultimos_3_meses':
          inicio = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString();
          fin = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString();
          break;
        case 'personalizado':
          inicio = fechaInicio ? new Date(fechaInicio).toISOString() : '';
          fin = fechaFin ? new Date(fechaFin + 'T23:59:59').toISOString() : '';
          break;
        default:
          break;
      }

      const params = new URLSearchParams();
      if (inicio) params.append('fecha_inicio', inicio);
      if (fin) params.append('fecha_fin', fin);
      if (areaSeleccionada && areaSeleccionada !== 'all') params.append('area', areaSeleccionada);
      if (funcionarioSeleccionado) params.append('funcionario_id', funcionarioSeleccionado);

      const { values, fallidos } = await settleRequests({
        'dashboard/metricas': api.get(`/dashboard/metricas?${params.toString()}`),
        'alertas':            api.get('/alertas?only_unread=true'),
      });

      if (values['dashboard/metricas']) setMetricas(values['dashboard/metricas'].data);
      if (values['alertas'])            setAlertas(values['alertas'].data || []);

      if (fallidos.length) {
        console.error('[Dashboard] Endpoints con error:', fallidos);
        fallidos.forEach(f => toast.error(`Dashboard · ${f.detalle}`, { duration: 8000 }));
      }

      // Cargar métricas de ventas si es socio o gestor comercial
      if (esGestorComercial(currentUser)) {
        try {
          const ventasRes = await api.get('/dashboard/metricas-ventas');
          setMetricasVentas(ventasRes.data);
        } catch (err) {
          console.log('No tiene permisos para ver métricas de ventas');
        }
      }
    } catch (error) {
      console.error('[Dashboard] Error inesperado:', error);
      toast.error('Error inesperado en el Dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Marcar alerta como leída
  const marcarAlertaLeida = async (alertaId, e) => {
    e.stopPropagation(); // Evitar que el click navegue
    try {
      await api.put(`/alertas/${alertaId}/marcar-leida`);
      // Remover la alerta de la lista local
      setAlertas(prev => prev.filter(a => a.id !== alertaId));
      toast.success('Alerta marcada como leída');
    } catch (error) {
      toast.error('Error al marcar alerta');
    }
  };

  // Determinar si es Socio/Admin
  const isSocio = currentUser?.areas?.includes('Socios') || currentUser?.role === 'Administrador';
  const isLider = currentUser?.role === 'Líder' || currentUser?.role === 'Lider' || currentUser?.role === 'Coordinador';

  // Datos para gráfico de torta
  const pieData = useMemo(() => {
    if (!metricas?.distribucion_estados) return [];
    const estados = metricas.distribucion_estados;
    return [
      { name: 'Pendiente', value: estados.Pendiente || 0, color: COLORS.ambar },
      { name: 'En Progreso', value: estados['En Progreso'] || 0, color: '#8b5cf6' }, // Púrpura
      { name: 'En Revisión', value: estados['En Revisión'] || 0, color: COLORS.azulElectrico },
      { name: 'Devuelta', value: estados.Devuelta || 0, color: COLORS.rojo },
      { name: 'Cumplida', value: estados.Aprobada || 0, color: COLORS.verdeEsmeralda }
    ].filter(d => d.value > 0);
  }, [metricas]);

  // Toggle área expandida
  const toggleAreaExpand = (area) => {
    setExpandedAreas(prev => ({ ...prev, [area]: !prev[area] }));
  };

  // Navegación click-to-action
  const navigateTo = (path, params = {}) => {
    navigate(path, { state: params });
  };

  // Renderizar KPI Card con Tooltip - Premium Style
  const KPICard = ({ icon: Icon, label, value, subvalue, color, bgColor, onClick, trend, testId, tooltipText }) => (
    <TooltipProvider delayDuration={300}>
      <ShadcnTooltip>
        <TooltipTrigger asChild>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.02, y: -2 }}
            className="cursor-pointer"
            onClick={onClick}
          >
            <Card 
              className={`p-5 shadow-lg hover:shadow-xl transition-all backdrop-blur-sm ${bgColor}`}
              style={{ background: 'rgba(22, 32, 66, 0.6)' }}
              data-testid={testId}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="text-white/90 text-sm font-medium">{label}</p>
                    <p className="text-white text-3xl font-bold">{value}</p>
                    {subvalue && <p className="text-white/70 text-xs mt-1">{subvalue}</p>}
                  </div>
                </div>
                {trend !== undefined && (
                  <div className={`flex items-center gap-1 ${trend >= 0 ? 'text-[#00E676]' : 'text-[#FF5252]'}`}>
                    {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    <span className="text-sm font-medium">{Math.abs(trend)}%</span>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="bg-[#162042] text-white border-[#3A86FF]/30">
          <p className="text-xs">{tooltipText || 'Click para ver detalle'}</p>
        </TooltipContent>
      </ShadcnTooltip>
    </TooltipProvider>
  );

  // Renderizar fila de funcionario en tabla jerarquica - Premium Style
  const FuncionarioRow = ({ func, isChild = false }) => {
    const cumplimiento = func.porcentaje_cumplimiento || 0;
    const colorCumplimiento = cumplimiento >= 80 ? COLORS.verdeEsmeralda : cumplimiento >= 50 ? COLORS.ambar : COLORS.rojo;
    
    return (
      <tr 
        className={`border-b border-[#3A86FF]/10 hover:bg-[#3A86FF]/5 cursor-pointer transition-colors ${isChild ? 'bg-[#0B132B]/50' : ''}`}
        onClick={() => {
          setFuncionarioSeleccionado(func.id);
          setAreaSeleccionada('all');
        }}
      >
        <td className={`py-3 px-4 ${isChild ? 'pl-10' : ''}`}>
          <div className="flex items-center gap-2">
            {isChild && <ChevronRight className="w-3 h-3 text-white/30" />}
            <span className="text-white font-medium">{func.nombre}</span>
          </div>
          <span className="text-white/70 text-xs">{func.cargo}</span>
        </td>
        <td className="py-3 px-4 text-center">
          <span className="bg-[#FFB300]/20 text-[#FFB300] px-3 py-1 rounded-full text-sm font-semibold">
            {func.pendientes}
          </span>
        </td>
        <td className="py-3 px-4 text-center">
          <span className="bg-[#00E676]/20 text-[#00E676] px-3 py-1 rounded-full text-sm font-semibold">
            {func.cumplidas}
          </span>
        </td>
        <td className="py-3 px-4 text-center">
          {(func.en_revision || 0) > 0 ? (
            <span className="bg-[#3A86FF]/20 text-[#3A86FF] px-3 py-1 rounded-full text-sm font-semibold">
              {func.en_revision}
            </span>
          ) : (
            <span className="text-white/60">0</span>
          )}
        </td>
        <td className="py-3 px-4 text-center">
          {func.vencidas > 0 ? (
            <span className="bg-[#FF5252]/20 text-[#FF5252] px-3 py-1 rounded-full text-sm font-semibold">
              {func.vencidas}
            </span>
          ) : (
            <span className="text-white/60">0</span>
          )}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-[#0B132B] rounded-full h-2 overflow-hidden border border-[#3A86FF]/10">
              <div 
                className="h-full rounded-full transition-all"
                style={{ width: `${cumplimiento}%`, backgroundColor: colorCumplimiento }}
              />
            </div>
            <span className="text-white text-sm font-medium w-12 text-right">{cumplimiento}%</span>
          </div>
        </td>
      </tr>
    );
  };

  if (loading && !metricas) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B132B' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 blur-xl bg-[#00D4FF]/30 rounded-full" />
            <RefreshCw className="w-12 h-12 text-[#00D4FF] animate-spin relative z-10" />
          </div>
          <p className="text-white text-lg font-medium">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0B132B' }}>
      <Header />
      <div className="flex flex-1">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />

        <div className="flex-1 overflow-y-auto max-h-screen" style={{ transform: 'scale(0.95)', transformOrigin: 'top left', width: '105.26%' }}>
          {/* Dashboard Container con fondo premium */}
          <div 
            className="min-h-full p-6 relative"
            style={{ 
              background: 'linear-gradient(135deg, #0B132B 0%, #0F1A36 50%, #0B132B 100%)'
            }}
          >
            {/* Tech grid background */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-30"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(58, 134, 255, 0.03) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(58, 134, 255, 0.03) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px'
              }}
            />
            
            {/* Header con saludo y filtros */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 relative z-10"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Saludo personalizado */}
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-white" data-testid="dashboard-saludo">
                    Hola <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3A86FF] to-[#00D4FF]">{getNombreMostrar()}</span>...
                  </h1>
                  <p className="text-white/80 text-lg mt-1">estos son tus numeros.</p>
                </div>

                {/* Filtros */}
                <div className="flex flex-wrap items-center gap-3">
                  {/* Selector de rango de fecha */}
                  <Select value={rangoFecha} onValueChange={setRangoFecha}>
                    <SelectTrigger 
                      className="w-44 text-white border-[#3A86FF]/20 hover:border-[#3A86FF]/40 transition-colors"
                      style={{ background: 'rgba(22, 32, 66, 0.8)' }}
                    >
                      <Calendar className="w-4 h-4 mr-2 text-[#00D4FF]" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-[#3A86FF]/20" style={{ background: '#162042' }}>
                      <SelectItem value="este_mes" className="text-white hover:bg-[#3A86FF]/20">Este mes</SelectItem>
                      <SelectItem value="mes_anterior" className="text-white hover:bg-[#3A86FF]/20">Mes anterior</SelectItem>
                      <SelectItem value="ultimos_3_meses" className="text-white hover:bg-[#3A86FF]/20">Ultimos 3 meses</SelectItem>
                      <SelectItem value="personalizado" className="text-white hover:bg-[#3A86FF]/20">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>

                  {rangoFecha === 'personalizado' && (
                    <>
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        className="px-3 py-2 rounded-md text-white text-sm border border-[#3A86FF]/20"
                        style={{ background: 'rgba(22, 32, 66, 0.8)' }}
                      />
                      <span className="text-white/30">a</span>
                      <input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        className="px-3 py-2 rounded-md text-white text-sm border border-[#3A86FF]/20"
                        style={{ background: 'rgba(22, 32, 66, 0.8)' }}
                      />
                    </>
                  )}

                  {/* Selector de area (solo para Socios) */}
                  {isSocio && (
                    <Select value={areaSeleccionada} onValueChange={(v) => { setAreaSeleccionada(v); setFuncionarioSeleccionado(''); }}>
                      <SelectTrigger 
                        className="w-44 text-white border-[#3A86FF]/20 hover:border-[#3A86FF]/40 transition-colors"
                        style={{ background: 'rgba(22, 32, 66, 0.8)' }}
                      >
                        <Briefcase className="w-4 h-4 mr-2 text-[#00D4FF]" />
                        <SelectValue placeholder="Todas las areas" />
                      </SelectTrigger>
                      <SelectContent className="border-[#3A86FF]/20" style={{ background: '#162042' }}>
                        <SelectItem value="all" className="text-white hover:bg-[#3A86FF]/20">Todas las areas</SelectItem>
                        {metricas?.areas_disponibles?.map(area => (
                          <SelectItem key={area} value={area} className="text-white hover:bg-[#3A86FF]/20">{area}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  {funcionarioSeleccionado && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFuncionarioSeleccionado('')}
                      className="text-white/40 hover:text-white hover:bg-[#3A86FF]/20"
                    >
                      x Limpiar filtro
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* KPIs Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8 relative z-10">
              <KPICard
                icon={ClipboardList}
                label="Tareas Pendientes"
                value={metricas?.resumen?.tareas_pendientes || 0}
                subvalue={`${metricas?.resumen?.tareas_vencidas || 0} vencidas`}
                bgColor="bg-gradient-to-br from-[#FFB300]/20 to-[#FF8F00]/10 border border-[#FFB300]/30"
                onClick={() => navigateTo('/mis-tareas')}
                testId="kpi-pendientes"
                tooltipText="Ver todas las tareas pendientes"
              />
              <KPICard
                icon={AlertTriangle}
                label="Tareas Vencidas"
                value={metricas?.resumen?.tareas_vencidas || 0}
                bgColor="bg-gradient-to-br from-[#FF5252]/20 to-[#D32F2F]/10 border border-[#FF5252]/30"
                onClick={() => navigateTo('/mis-tareas', { filtro: 'vencidas' })}
                testId="kpi-vencidas"
                tooltipText="Ver tareas con plazo vencido"
              />
              <KPICard
                icon={CheckCircle2}
                label="Cumplidas este mes"
                value={metricas?.resumen?.tareas_cumplidas || 0}
                bgColor="bg-gradient-to-br from-[#00E676]/20 to-[#00C853]/10 border border-[#00E676]/30"
                onClick={() => navigateTo('/mis-tareas-realizadas')}
                testId="kpi-cumplidas"
                tooltipText="Ver historial de tareas aprobadas"
              />
              <KPICard
                icon={Clock}
                label="En Revision"
                value={metricas?.resumen?.tareas_en_revision || 0}
                bgColor="bg-gradient-to-br from-[#3A86FF]/20 to-[#2962FF]/10 border border-[#3A86FF]/30"
                onClick={() => navigateTo('/tareas-en-revision')}
                testId="kpi-revision"
                tooltipText="Ver tareas pendientes de aprobacion"
              />
              <KPICard
                icon={Users}
                label="Clientes Asignados"
                value={metricas?.resumen?.clientes_asignados || 0}
                subvalue="Carga mensual"
                bgColor="bg-gradient-to-br from-[#00D4FF]/20 to-[#00B0FF]/10 border border-[#00D4FF]/30"
                onClick={() => navigateTo('/contacto-clientes')}
                testId="kpi-clientes"
                tooltipText="Gestionar contacto con clientes"
              />
            </div>

            {/* Widget de Metricas de Ventas - Solo para Socios */}
            {esSocioVentas && metricasVentas && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8 relative z-10"
              >
                <Card 
                  className="border border-[#3A86FF]/20 overflow-hidden backdrop-blur-sm" 
                  style={{ background: 'linear-gradient(135deg, rgba(22, 32, 66, 0.8) 0%, rgba(15, 26, 54, 0.9) 100%)' }}
                  data-testid="widget-ventas"
                >
                  <div className="p-6">
                    {/* Header del Widget */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
                          <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white">Dashboard Ejecutivo de Ventas</h2>
                          <p className="text-white/70 text-sm">Métricas Comerciales - {metricasVentas.mes_actual}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/60">Valor UF: ${metricasVentas.valor_uf?.toLocaleString('es-CL')}</p>
                      </div>
                    </div>

                    {/* Grid Principal */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Columna 1: Embudo de Propuestas */}
                      <div className="lg:col-span-1">
                        <div className="bg-[#0B132B]/50 rounded-xl p-5 h-full border border-[#3A86FF]/10">
                          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-400" />
                            Pipeline de Propuestas
                          </h3>
                          <div className="space-y-3">
                            {[
                              { estado: 'Borrador', color: 'bg-white/30', textColor: 'text-white/80' },
                              { estado: 'En Negociación', color: 'bg-amber-500', textColor: 'text-amber-300' },
                              { estado: 'Enviada', color: 'bg-blue-500', textColor: 'text-blue-300' },
                              { estado: 'Aceptada', color: 'bg-emerald-500', textColor: 'text-emerald-300' },
                              { estado: 'Rechazada', color: 'bg-red-500', textColor: 'text-red-300' }
                            ].map(({ estado, color, textColor }) => (
                              <div key={estado} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full ${color}`} />
                              <span className="text-white/80 text-sm">{estado}</span>
                                </div>
                                <span className={`${textColor} font-bold text-lg`}>
                                  {metricasVentas.pipeline?.[estado] || 0}
                                </span>
                              </div>
                            ))}
                            <div className="pt-3 mt-3 border-t border-[#3A86FF]/20">
                              <div className="flex items-center justify-between">
                                <span className="text-white font-medium">Total PSPs</span>
                                <span className="text-white font-bold text-xl">{metricasVentas.total_propuestas}</span>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-white/80 text-xs">Tasa de Conversión</span>
                                <span className="text-emerald-400 font-semibold">{metricasVentas.tasa_conversion}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Columna 2: Métricas Financieras */}
                      <div className="lg:col-span-1">
                        <div className="bg-[#0B132B]/50 rounded-xl p-5 h-full border border-[#3A86FF]/10">
                          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-emerald-400" />
                            Ingresos Proyectados
                          </h3>
                          <div className="space-y-4">
                            {/* PSP Aceptadas */}
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-emerald-300 text-sm font-medium">Ingresos Confirmados</span>
                                <span className="text-emerald-400 text-xs">({metricasVentas.metricas_financieras?.aceptadas?.cantidad || 0} PSPs)</span>
                              </div>
                              <p className="text-emerald-400 text-2xl font-bold">
                                {(metricasVentas.metricas_financieras?.aceptadas?.total_uf || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })} UF
                              </p>
                              <p className="text-emerald-300/70 text-xs">
                                ${(metricasVentas.metricas_financieras?.aceptadas?.total_clp || 0).toLocaleString('es-CL')} CLP
                              </p>
                            </div>

                            {/* PSP En Negociación */}
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-amber-300 text-sm font-medium">En Cierre</span>
                                <span className="text-amber-400 text-xs">({metricasVentas.metricas_financieras?.en_negociacion?.cantidad || 0} PSPs)</span>
                              </div>
                              <p className="text-amber-400 text-2xl font-bold">
                                {(metricasVentas.metricas_financieras?.en_negociacion?.total_uf || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })} UF
                              </p>
                              <p className="text-amber-300/70 text-xs">
                                ${(metricasVentas.metricas_financieras?.en_negociacion?.total_clp || 0).toLocaleString('es-CL')} CLP
                              </p>
                            </div>

                            {/* PSP Enviadas */}
                            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-blue-300 text-sm font-medium">Esperando Respuesta</span>
                                <span className="text-blue-400 text-xs">({metricasVentas.metricas_financieras?.enviadas?.cantidad || 0} PSPs)</span>
                              </div>
                              <p className="text-blue-400 text-2xl font-bold">
                                {(metricasVentas.metricas_financieras?.enviadas?.total_uf || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })} UF
                              </p>
                              <p className="text-blue-300/70 text-xs">
                                ${(metricasVentas.metricas_financieras?.enviadas?.total_clp || 0).toLocaleString('es-CL')} CLP
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Columna 3: Proyección Total + Clientes Nuevos */}
                      <div className="lg:col-span-1 flex flex-col gap-4">
                        {/* Proyección Total */}
                        <div className="bg-gradient-to-br from-emerald-600/20 to-teal-600/20 border border-emerald-500/30 rounded-xl p-5 flex-1">
                          <h3 className="text-emerald-300 text-sm font-medium mb-2">Proyección Total del Período</h3>
                          <p className="text-white text-4xl font-bold mb-1">
                            {(metricasVentas.proyeccion_total?.total_uf || 0).toLocaleString('es-CL', { minimumFractionDigits: 2 })} UF
                          </p>
                          <p className="text-emerald-300/80 text-lg">
                            ${(metricasVentas.proyeccion_total?.total_clp || 0).toLocaleString('es-CL')} CLP
                          </p>
                          <div className="mt-3 pt-3 border-t border-emerald-500/30">
                            <p className="text-xs text-white/70">Incluye PSPs Aceptadas + En Negociación + Enviadas</p>
                          </div>
                        </div>

                        {/* Clientes Nuevos del Mes */}
                        <div className="bg-gradient-to-br from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-xl p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-purple-300 text-sm font-medium mb-1">Clientes Nuevos del Mes</h3>
                              <p className="text-white text-4xl font-bold">{metricasVentas.clientes_nuevos_mes || 0}</p>
                              <p className="text-purple-300/90 text-xs mt-1">Prospectos convertidos a Clientes Activos</p>
                            </div>
                            <div className="p-3 rounded-full bg-purple-500/20">
                              <Users className="w-8 h-8 text-purple-400" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Contenido principal: Graficos + Alertas */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 relative z-10">
              
              {/* Grafico de Torta - Distribucion de Estados */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card 
                  className="p-6 h-full border border-[#3A86FF]/20 backdrop-blur-sm"
                  style={{ background: 'rgba(22, 32, 66, 0.6)' }}
                >
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-[#00D4FF]" />
                    Distribucion de Tareas
                  </h3>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPie>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={3}
                          dataKey="value"
                          onClick={(data) => {
                            if (data.name === 'Pendiente') navigateTo('/mis-tareas');
                            else if (data.name === 'En Revision') navigateTo('/tareas-en-revision');
                            else if (data.name === 'Cumplida') navigateTo('/mis-tareas-realizadas');
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#162042', border: '1px solid rgba(58,134,255,0.3)', borderRadius: '8px' }}
                          labelStyle={{ color: 'white' }}
                        />
                      </RechartsPie>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-white/40">
                      Sin datos para mostrar
                    </div>
                  )}
                  {/* Leyenda */}
                  <div className="flex flex-wrap justify-center gap-4 mt-4">
                    {pieData.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-white/80 text-sm">{entry.name}: {entry.value}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>

              {/* Grafico de Barras - Progreso de Metas */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card 
                  className="p-6 h-full border border-[#3A86FF]/20 backdrop-blur-sm"
                  style={{ background: 'rgba(22, 32, 66, 0.6)' }}
                >
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#00E676]" />
                    Progreso del Periodo
                  </h3>
                  <div className="space-y-6">
                    {/* Barra de cumplimiento */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/80">Tareas Cumplidas</span>
                        <span className="text-[#00E676] font-semibold">{metricas?.resumen?.tareas_cumplidas || 0}</span>
                      </div>
                      <div className="h-4 bg-[#0B132B] rounded-full overflow-hidden border border-[#3A86FF]/10">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(((metricas?.resumen?.tareas_cumplidas || 0) / Math.max((metricas?.resumen?.carga_total || 1), 1)) * 100, 100)}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className="h-full bg-gradient-to-r from-[#00E676] to-[#00C853] rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Barra de pendientes */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/80">Tareas Pendientes</span>
                        <span className="text-[#FFB300] font-semibold">{metricas?.resumen?.tareas_pendientes || 0}</span>
                      </div>
                      <div className="h-4 bg-[#0B132B] rounded-full overflow-hidden border border-[#3A86FF]/10">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(((metricas?.resumen?.tareas_pendientes || 0) / Math.max((metricas?.resumen?.carga_total || 1), 1)) * 100, 100)}%` }}
                          transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                          className="h-full bg-gradient-to-r from-[#FFB300] to-[#FF8F00] rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Barra de en revision */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/80">En Revision</span>
                        <span className="text-[#3A86FF] font-semibold">{metricas?.resumen?.tareas_en_revision || 0}</span>
                      </div>
                      <div className="h-4 bg-[#0B132B] rounded-full overflow-hidden border border-[#3A86FF]/10">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(((metricas?.resumen?.tareas_en_revision || 0) / Math.max((metricas?.resumen?.carga_total || 1), 1)) * 100, 100)}%` }}
                          transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
                          className="h-full bg-gradient-to-r from-[#3A86FF] to-[#00D4FF] rounded-full"
                        />
                      </div>
                    </div>
                    
                    {/* Barra de vencidas */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-white/80">Tareas Vencidas</span>
                        <span className="text-[#FF5252] font-semibold">{metricas?.resumen?.tareas_vencidas || 0}</span>
                      </div>
                      <div className="h-4 bg-[#0B132B] rounded-full overflow-hidden border border-[#3A86FF]/10">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(((metricas?.resumen?.tareas_vencidas || 0) / Math.max((metricas?.resumen?.tareas_pendientes || 1), 1)) * 100, 100)}%` }}
                          transition={{ duration: 1, ease: "easeOut", delay: 0.4 }}
                          className="h-full bg-gradient-to-r from-[#FF5252] to-[#D32F2F] rounded-full"
                        />
                      </div>
                    </div>

                    {/* Indicador de meta */}
                    <div className="pt-4 border-t border-[#3A86FF]/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-[#00D4FF]" />
                          <span className="text-white/80 text-sm">Meta del mes</span>
                        </div>
                        <span className="text-white font-bold text-lg">
                          {metricas?.resumen?.carga_total ? 
                            Math.round(((metricas?.resumen?.tareas_cumplidas || 0) / metricas.resumen.carga_total) * 100) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Panel de Alertas */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card 
                  className="p-6 h-full border border-[#3A86FF]/20 backdrop-blur-sm"
                  style={{ background: 'rgba(22, 32, 66, 0.6)' }}
                >
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-[#FFB300]" />
                    Alertas No Leidas
                    {alertas.length > 0 && (
                      <span className="ml-auto bg-[#FF5252]/20 text-[#FF5252] text-xs px-2 py-0.5 rounded-full border border-[#FF5252]/30">
                        {alertas.length}
                      </span>
                    )}
                  </h3>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {alertas.length > 0 ? (
                      alertas.slice(0, 6).map((alerta, index) => (
                        <motion.div
                          key={alerta.id || index}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`p-3 rounded-lg border-l-4 cursor-pointer hover:bg-white/5 transition-colors relative group ${
                            alerta.tipo === 'vencida' || alerta.alert_type === 'overdue'
                              ? 'bg-[#FF5252]/10 border-[#FF5252]'
                              : 'bg-[#FFB300]/10 border-[#FFB300]'
                          }`}
                          onClick={() => navigateTo('/mis-tareas')}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium line-clamp-1">{alerta.titulo || alerta.title}</p>
                              <p className="text-white/70 text-xs mt-1 line-clamp-2">{alerta.mensaje || alerta.area}</p>
                            </div>
                            <button
                              onClick={(e) => marcarAlertaLeida(alerta.id, e)}
                              className="p-1.5 rounded-full hover:bg-[#00E676]/20 transition-colors opacity-0 group-hover:opacity-100"
                              title="Marcar como leida"
                            >
                              <CheckCircle2 className="w-4 h-4 text-[#00E676]" />
                            </button>
                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <CheckCircle2 className="w-12 h-12 text-[#00E676] mx-auto mb-3" />
                        <p className="text-white/70">Sin alertas pendientes</p>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Tabla Jerarquica - Solo para Lideres y Socios */}
            {(isLider || isSocio) && metricas?.ranking_equipo && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mb-8 relative z-10"
              >
                <Card 
                  className="border border-[#3A86FF]/20 overflow-hidden backdrop-blur-sm"
                  style={{ background: 'rgba(22, 32, 66, 0.6)' }}
                >
                  <div className="p-6 border-b border-[#3A86FF]/20">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Award className="w-5 h-5 text-[#FFB300]" />
                      {isSocio ? 'Rendimiento por Area y Funcionario' : 'Rendimiento del Equipo'}
                    </h3>
                    <p className="text-white/70 text-sm mt-1">Click en una fila para ver detalle individual</p>
                  </div>

                  {/* Vista por Areas para Socio */}
                  {isSocio && metricas?.metricas_por_area && areaSeleccionada === 'all' ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-white/60 text-sm" style={{ background: 'rgba(22, 32, 66, 0.8)' }}>
                            <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-xs">Area / Funcionario</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Pendientes</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Cumplidas</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">En Revision</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Vencidas</th>
                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">% Cumplimiento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(metricas.metricas_por_area).map(([area, data]) => {
                            const isExpanded = expandedAreas[area];
                            const funcionariosArea = metricas.ranking_equipo?.filter(f => f.area === area) || [];
                            const cumplimiento = data.porcentaje_cumplimiento || 0;
                            const colorCumplimiento = cumplimiento >= 80 ? COLORS.verdeEsmeralda : cumplimiento >= 50 ? COLORS.ambar : COLORS.rojo;
                            
                            return (
                              <React.Fragment key={area}>
                                {/* Fila del area */}
                                <tr 
                                  className="border-b border-[#3A86FF]/10 hover:bg-[#3A86FF]/5 cursor-pointer transition-colors"
                                  style={{ background: 'rgba(28, 41, 81, 0.4)' }}
                                  onClick={() => toggleAreaExpand(area)}
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      {funcionariosArea.length > 0 && (
                                        isExpanded ? 
                                          <ChevronDown className="w-4 h-4 text-[#00D4FF]" /> : 
                                          <ChevronRight className="w-4 h-4 text-white/40" />
                                      )}
                                      <Briefcase className="w-4 h-4 text-[#3A86FF]" />
                                      <span className="text-white font-semibold">{area}</span>
                                      <span className="text-white/70 text-sm">({funcionariosArea.length} funcionarios)</span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <span className="bg-[#FFB300]/20 text-[#FFB300] px-3 py-1 rounded-full text-sm font-semibold">
                                      {data.pendientes}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <span className="bg-[#00E676]/20 text-[#00E676] px-3 py-1 rounded-full text-sm font-semibold">
                                      {data.cumplidas}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {(data.en_revision || 0) > 0 ? (
                                      <span className="bg-[#3A86FF]/20 text-[#3A86FF] px-3 py-1 rounded-full text-sm font-semibold">
                                        {data.en_revision}
                                      </span>
                                    ) : (
                                      <span className="text-white/60">0</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    {data.vencidas > 0 ? (
                                      <span className="bg-[#FF5252]/20 text-[#FF5252] px-3 py-1 rounded-full text-sm font-semibold">
                                        {data.vencidas}
                                      </span>
                                    ) : (
                                      <span className="text-white/60">0</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 bg-[#0B132B] rounded-full h-2 overflow-hidden border border-[#3A86FF]/10">
                                        <div 
                                          className="h-full rounded-full transition-all"
                                          style={{ width: `${cumplimiento}%`, backgroundColor: colorCumplimiento }}
                                        />
                                      </div>
                                      <span className="text-white text-sm font-medium w-12 text-right">{cumplimiento}%</span>
                                    </div>
                                  </td>
                                </tr>
                                
                                {/* Filas de funcionarios (expandidas) */}
                                {isExpanded && funcionariosArea.map(func => (
                                  <FuncionarioRow key={func.id} func={func} isChild={true} />
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    /* Vista de funcionarios (para Lideres o cuando hay area seleccionada) */
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-white/60 text-sm" style={{ background: 'rgba(22, 32, 66, 0.8)' }}>
                            <th className="text-left py-3 px-4 font-semibold uppercase tracking-wider text-xs">Funcionario</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Pendientes</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Cumplidas</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">En Revision</th>
                            <th className="text-center py-3 px-4 font-semibold uppercase tracking-wider text-xs">Vencidas</th>
                            <th className="py-3 px-4 font-semibold uppercase tracking-wider text-xs">% Cumplimiento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metricas.ranking_equipo?.map(func => (
                            <FuncionarioRow key={func.id} func={func} />
                          ))}
                          {(!metricas.ranking_equipo || metricas.ranking_equipo.length === 0) && (
                            <tr>
                              <td colSpan={5} className="py-8 text-center text-white/70">
                                No hay datos de equipo disponibles
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </motion.div>
            )}

            {/* Tareas asignadas a otras areas - Solo para Lideres */}
            {isLider && metricas?.tareas_otras_areas && metricas.tareas_otras_areas.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="relative z-10"
              >
                <Card 
                  className="p-6 border border-[#3A86FF]/20 backdrop-blur-sm"
                  style={{ background: 'rgba(22, 32, 66, 0.6)' }}
                >
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00D4FF]" />
                    Tareas Asignadas a Otras Areas
                  </h3>
                  <div className="space-y-2">
                    {metricas.tareas_otras_areas.map((tarea, idx) => (
                      <div 
                        key={tarea.id || idx}
                        className="flex items-center justify-between p-3 bg-[#0B132B]/50 rounded-lg hover:bg-[#3A86FF]/10 cursor-pointer transition-colors border border-[#3A86FF]/10"
                        onClick={() => navigateTo('/mis-tareas')}
                      >
                        <div>
                          <p className="text-white text-sm font-medium">{tarea.titulo}</p>
                          <p className="text-white/70 text-xs">{tarea.area} - {tarea.responsable}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          tarea.estado === 'Aprobada' ? 'bg-[#00E676]/20 text-[#00E676]' :
                          tarea.estado === 'Pendiente' ? 'bg-[#FFB300]/20 text-[#FFB300]' :
                          'bg-white/10 text-white/60'
                        }`}>
                          {tarea.estado}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
